import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMonitoringStatus } from '@/domains/landing-page/hooks/useMonitoringStatus';
import { ListEventTypes } from '@/domains/traffic-monitoring/constants/listEvent.constant';
import { formatDateToDDMonYYYY, formatTimeOnly } from '@/shared/utils/date';
import { useColorStore } from '@/store/colorStore';

const MonitoringStatus = () => {
  const { NEWCOLOR } = useColorStore();
  const { reportCode } = useParams<{ reportCode: string }>();
  const { patients } = useMonitoringStatus(reportCode);
  const [activePatient, setActivePatient] = useState(1);

  // Positional "Patient N" tabs, one per victim/ambulance still on the report
  // (see get_monitoring_status.py) - there is no patient name to show here,
  // same as the mock this replaces.
  const patientTabs = patients.map((patient, index) => ({
    id: index + 1,
    title: `Patient ${index + 1}`,
    ambulance: patient.ambulance_police_number ?? '-',
    eventType: patient.event_type,
    eventTypeLabel: patient.event_type_label,
    facility: patient.health_care_facility_name,
    updatedAt: patient.updated_at,
  }));
  const activePatientData = patientTabs.find((patient) => patient.id === activePatient);

  const eventTypes = activePatientData?.eventType ?? 'incoming';
  const details = activePatientData
    ? activePatientData.facility
      ? `${activePatientData.eventTypeLabel} - ${activePatientData.facility}`
      : activePatientData.eventTypeLabel
    : '-';
  const timeStamp = activePatientData?.updatedAt
    ? `${formatDateToDDMonYYYY(activePatientData.updatedAt)} ${formatTimeOnly(activePatientData.updatedAt)}`
    : '-';

  return (
    <section className="w-full h-screen bg-[#F3F6FE] p-6">
      <div className="p-6 shadow-[0_4px_4px_0_rgba(0,0,0,0.16)] bg-db-card bg-db-border rounded-[12px] w-full h-fit flex flex-col gap-6">
        <div className="flex flex-col gap-2 justify-center items-center">
          <h3 className="h5bold text-center">Monitoring Status Update</h3>

          <p className="title2 text-db-text-grey text-center">
            Track the latest updates of your report
          </p>
        </div>

        <div className="inline-flex gap-3 items-center">
          {patientTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActivePatient(tab.id)}
              className={`px-4 py-2 border title2 rounded-full ${
                activePatient === tab.id
                  ? 'text-white bg-[#007AFF] border-transparent'
                  : 'bg-[#F3F6FE] border-[#C2D2E2] text-text-grey'
              }`}
            >
              {tab.title}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 justify-center border w-full border-[#C2D2E2] rounded-[12px] p-4">
          <h4 className="title text-text-blue-100 uppercase text-start">Details</h4>

          <div className="inline-flex justify-between flex-wrap gap-3 items-center">
            <div className="flex flex-col gap-1">
              <span className="text-text-grey body1">Ambulance</span>

              <span className="text-db-text dark:text-white text-center title1medium py-1 px-2 border rounded-[4px] dark:bg-[#F3F6FE]/10 bg-[#F3F6FE]">
                {activePatientData?.ambulance}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-text-grey body1">Status</span>

              <span
                className="w-fit p-2 rounded-full title2 uppercase text-white shrink-0"
                style={{
                  backgroundColor: NEWCOLOR[ListEventTypes[eventTypes].color],
                }}
              >
                {eventTypes}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-text-grey body1">Details</span>
            <p className="title2 text-black">{details}</p>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-text-grey body1">Timestamp</span>
            <p className="title2 text-black">{timeStamp}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MonitoringStatus;
